import React, { useState, useMemo } from "react";
import Select from "../../../../shared/Select";
import { useAssessmentScale } from "../../../../../hooks/useAssessmentScale";
import Button from "../../../../shared/Button";
import CustomModal from "../../../../shared/CustomModal";
import ConfigureRubricsBasedAssessment from "./ConfigureRubricsBasedAssessment";
import swal from "sweetalert";
import { Spinner } from "reactstrap";
import { components } from "react-select";
import { URL } from "../../../../../../../utils/Constants";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DragIndicator } from "@mui/icons-material";
import { Tooltip } from "@mui/material";

export const assessmentTypeOptions = [
  {
    label: "Rubrics Based Assessment",
    value: "rubricsBasedAssessment",
  },
  {
    label: "Overall Assessment",
    value: "overallAssessment",
  },
  {
    label: "Both",
    value: "both",
  },
];

const AssessmentConfiguration = ({
  assessmentFormData,
  setAssessmentFormData,
  assessorOptions,
  loading,
  assessmentTableData,
  setAssessmentTableData,
  assessorRoleOption,
  rubricsConfig,
  setRubricsConfig,
  assessorConfig,
  setDeletedItem,
  isImporting,
}) => {
  const { assessmentScale } = useAssessmentScale({
    assessmentScaleTypeCode: "sct-lvl",
  });
  const [configureRubricsAssessmentModal, setConfigureRubricsAssessmentModal] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [assessmentScaleForRubricsConfig, setAssesmentScaleForRubricsConfig] =
    useState(null);

  const [editableRowId, setEditableRowId] = useState(null);
  const [newEditedData, setNewEditedData] = useState({});

  //assessment scale options
  const assessmentScaleOptions = useMemo(() => {
    if (assessmentScale?.length === 0) return [];

    return assessmentScale?.map((item) => ({
      label: item?.scaleName,
      value: item?.id,
    }));
  }, [assessmentScale]);

  //assessmentscale config to extract data faster
  const assesmentScaleConfig = useMemo(() => {
    const map = {};

    if (assessmentScale?.length) {
      assessmentScale.forEach((item) => {
        map[item?.id] = item;
      });
    }

    return map;
  }, [assessmentScale]);

  //handle assessment form change
  const handleAssessmentDataChange = (name, value, option) => {
    if (name === "accessor") {
      if (!option?.roleCode && !option?.subRoleId) {
        setAssessmentFormData((prev) => ({
          ...prev,
          [name]: option?.value,
          roleCode: "",
          subRoleId: "",
          roleId: "",
        }));
      } else {
        setAssessmentFormData((prev) => ({
          ...prev,
          accessor: "",
          roleCode: option?.roleCode,
          subRoleId: option?.subRoleId,
          roleId: option?.value,
        }));
      }
      return;
    }

    setAssessmentFormData((prev) => ({
      ...prev,
      [name]: name === "header" ? value : option?.value,
    }));
  };

  //add assessment data to table
  const handleAddAssessmentToTable = () => {
    const assessorExists = assessmentTableData?.some(
      (td) =>
        td?.header?.trim()?.toLowerCase() ===
        assessmentFormData?.header?.trim()?.toLowerCase(),
    );

    if (assessorExists) {
      swal(
        "Oops",
        `Assements with name ${assessmentFormData?.header} already exists`,
      );
      return;
    }

    const fId = Date.now();
    const formData = { fId: fId, ...assessmentFormData };

    setAssessmentTableData((prev) => [...prev, formData]);

    setRubricsConfig((prev) => {
      let newData = [];
      if (
        assessmentFormData?.assessmentType === "rubricsBasedAssessment" ||
        assessmentFormData?.assessmentType === "both"
      ) {
        newData = [
          {
            name: "",
            desc: "",
          },
        ];
      }

      return {
        ...prev,
        [fId]: newData,
      };
    });

    setAssessmentFormData({
      header: "",
      assessmentType: "overallAssessment",
      overallScaleId: "",
      rubricsScaleId: "",
      accessor: "",
      subRoleId: "",
      roleCode: "",
      roleId: "",
    });
  };

  //remove assessment data from table
  const removeDataFromTable = (index, key, id) => {
    const newData = assessmentTableData?.filter((item, idx) => idx !== index);
    setAssessmentTableData(newData);

    //remove rubrics assessment
    setRubricsConfig((prev) => {
      if (!prev || !prev.hasOwnProperty(key)) return prev;

      const { [key]: _, ...newObject } = prev;
      return newObject;
    });

    if (id) {
      setDeletedItem((prev) => ({
        ...prev,
        deleteConfigIds: [...prev.deleteConfigIds, id],
      }));
    }

    return;
  };

  //assessment form validation
  const isAssessmentFormValid = () => {
    const {
      header,
      assessmentType,
      accessor,
      roleId,
      overallScaleId,
      rubricsScaleId,
    } = assessmentFormData;

    if (!header || !assessmentType) return false;
    if (!accessor && !roleId) return false;

    if (assessmentType === "both") {
      return overallScaleId && rubricsScaleId;
    }

    if (assessmentType === "overallAssessment") {
      return !!overallScaleId;
    }

    if (assessmentType === "rubricsBasedAssessment") {
      return !!rubricsScaleId;
    }

    return true;
  };

  //custom Select option style
  const CustomOption = (props) => {
    const { data } = props;

    return (
      <components.Option {...props}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              style={{ width: 20, marginRight: 10 }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.Option>
    );
  };

  //custom Select display value style
  const CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <div style={{ display: "flex", alignItems: "center" }}>
          {data.levelType === "image" ? (
            <img
              src={URL.imageSelectURL + data.label}
              alt=""
              style={{
                width: 20,
                height: 20,
                objectFit: "cover",
                marginRight: 10,
                borderRadius: "50%",
              }}
            />
          ) : (
            <span>{data.label}</span>
          )}
        </div>
      </components.SingleValue>
    );
  };

  //render assessment scale levels in select input in table
  const renderAssessmentScaleLevel = (id) => {
    if (!id) return;

    const scale = assesmentScaleConfig[id];
    const scaleOptions = scale?.assessmentScaleLevels?.map((item) => ({
      label: item?.level,
      value: item?.id,
      levelType: item?.levelType,
    }));

    return (
      <Select
        options={scaleOptions}
        styles={{ control: { minWidth: "150px" } }}
        components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
        isClearable={false}
      />
    );
  };

  //edit data handler
  const handleSaveEdit = (id) => {
    setAssessmentTableData((prev) =>
      prev.map((row) => (row?.id === id ? { ...row, ...newEditedData } : row)),
    );

    setEditableRowId(null);
    setNewEditedData({});
  };

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    if (
      result?.destination?.droppableId === result?.source?.droppableId &&
      result?.destination?.index === result?.source?.index
    )
      return;

    const items = Array.from(assessmentTableData);
    const [reorderedItem] = items.splice(result?.source?.index, 1);
    items.splice(result?.destination?.index, 0, reorderedItem);

    setAssessmentTableData(items);
  };

  return (
    <>
      <div style={{ flexDirection: "column" }} className="arr-form">
        <div style={{ marginBottom: "0" }} className="arr-heading">
          <h2>Assessment Configuration</h2>
        </div>

        <div className="arr-form noShadow">
          <div className="form-input">
            <input
              type="text"
              value={assessmentFormData?.header ?? ""}
              onChange={(e) =>
                handleAssessmentDataChange("header", e.target.value)
              }
              name="header"
              placeholder="Enter Assessment Header"
              className="form-control"
            />
          </div>

          <Select
            options={assessmentTypeOptions}
            value={
              assessmentTypeOptions?.find(
                (opt) => opt?.value === assessmentFormData?.assessmentType,
              ) || null
            }
            onChange={(option) =>
              handleAssessmentDataChange("assessmentType", null, option)
            }
            placeholder={"Select Assessment Type"}
            styles={{ control: { minWidth: "300px" } }}
            isClearable={false}
          />

          {(assessmentFormData?.assessmentType === "overallAssessment" ||
            assessmentFormData?.assessmentType === "both") && (
            <Select
              options={assessmentScaleOptions}
              value={
                assessmentScaleOptions?.find(
                  (opt) => opt.value === assessmentFormData?.overallScaleId,
                ) || null
              }
              onChange={(option) =>
                handleAssessmentDataChange("overallScaleId", null, option)
              }
              placeholder={"Select overall assessment scale"}
              styles={{ control: { minWidth: "300px" } }}
              isClearable={false}
            />
          )}

          {(assessmentFormData?.assessmentType === "rubricsBasedAssessment" ||
            assessmentFormData?.assessmentType === "both") && (
            <Select
              options={assessmentScaleOptions}
              value={
                assessmentScaleOptions?.find(
                  (opt) => opt.value === assessmentFormData?.rubricsScaleId,
                ) || null
              }
              onChange={(option) =>
                handleAssessmentDataChange("rubricsScaleId", null, option)
              }
              placeholder={"Select rubrics assessment Scale"}
              styles={{ control: { minWidth: "300px" } }}
              isClearable={false}
            />
          )}

          <Select
            options={assessorOptions}
            value={
              assessorOptions
                ?.flatMap((group) => group.options || [])
                .find(
                  (option) =>
                    option?.value ===
                    (assessmentFormData?.accessor ||
                      assessmentFormData?.roleId),
                ) || null
            }
            onChange={(option) =>
              handleAssessmentDataChange("accessor", null, option)
            }
            placeholder={"Select Assessor"}
            styles={{ control: { minWidth: "300px" } }}
            isClearable={false}
          />

          <Button
            onClick={handleAddAssessmentToTable}
            disabled={!isAssessmentFormValid()}
            style={{ display: "block" }}
          >
            Add
          </Button>
        </div>

        <div style={{ margin: 0 }} className="arr-table-container">
          <table className="arr-table">
            <thead>
              <tr>
                <th></th>
                <th>Assessment Header</th>
                <th>Assessment Type</th>
                <th>Assessment Scale</th>
                <th>Assessor</th>
                <th>Preview</th>
                <th></th>
              </tr>
            </thead>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="scale-config-list">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {loading.read || isImporting ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="arr-spinner">
                            <div className="loader">
                              <Spinner size={"md"} color="primary"></Spinner>
                            </div>
                            <div
                              style={{ fontSize: "13px", marginTop: "15px" }}
                            >
                              Please wait !! Processing.....
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : assessmentTableData?.length > 0 ? (
                      assessmentTableData?.map((item, index) => {
                        const isEditable = item?.id
                          ? editableRowId === item?.id
                          : null;

                        const currentData = isEditable
                          ? { ...item, ...newEditedData }
                          : item;

                        const staffRole = currentData?.subRoleId
                          ? assessorRoleOption?.find(
                              (opt) => opt.subRoleId === currentData?.subRoleId,
                            )?.label
                          : null;

                        const isDisabled =
                          currentData?.assessmentType ===
                            "rubricsBasedAssessment" ||
                          currentData?.assessmentType === "both"
                            ? currentData?.recorded ||
                              currentData?.rubrics?.some((r) => r?.recorded)
                            : currentData?.recorded;

                        const assessmentType =
                          currentData?.assessmentType ===
                          "rubricsBasedAssessment"
                            ? "Rubrics Based Assessment"
                            : currentData?.assessmentType ===
                                "overallAssessment"
                              ? "Overall Assessment"
                              : "Both";

                        return (
                          <Draggable
                            draggableId={String(item?.id ?? item?.fId)}
                            key={item?.id ?? item?.fId}
                            index={index}
                          >
                            {(provided) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <td>
                                  <div style={{ color: "#d1d5db" }}>
                                    <DragIndicator />
                                  </div>
                                </td>
                                <td>
                                  {isEditable ? (
                                    <input
                                      type="text"
                                      value={currentData?.header ?? ""}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          header: value,
                                        }));
                                      }}
                                      name="header"
                                      placeholder="Enter Assessment Header"
                                      className="form-control"
                                    />
                                  ) : (
                                    currentData?.header
                                  )}
                                </td>
                                <td>
                                  {isEditable ? (
                                    <Select
                                      options={assessmentTypeOptions}
                                      value={
                                        assessmentTypeOptions?.find(
                                          (opt) =>
                                            opt?.value ===
                                            newEditedData?.assessmentType,
                                        ) || null
                                      }
                                      onChange={(option) =>
                                        setNewEditedData((prev) => ({
                                          ...prev,
                                          assessmentType: option?.value,
                                        }))
                                      }
                                      placeholder={"Select Assessment Type"}
                                      styles={{
                                        control: { minWidth: "200px" },
                                      }}
                                      isClearable={false}
                                    />
                                  ) : (
                                    assessmentType
                                  )}
                                </td>
                                <td>
                                  <div
                                    className="flex"
                                    style={{
                                      flexDirection: "column",
                                      gap: "4px",
                                      alignItems: "start",
                                    }}
                                  >
                                    {(currentData?.assessmentType ===
                                      "overallAssessment" ||
                                      currentData?.assessmentType === "both") &&
                                      (isEditable ? (
                                        <Select
                                          options={assessmentScaleOptions}
                                          value={assessmentScaleOptions?.find(
                                            (opt) =>
                                              opt.value ===
                                              newEditedData?.overallScaleId,
                                          )}
                                          onChange={(option) => {
                                            setNewEditedData((prev) => ({
                                              ...prev,
                                              overallScaleId: option?.value,
                                            }));
                                          }}
                                          placeholder={"Overallscale scale"}
                                          styles={{
                                            control: { minWidth: "200px" },
                                          }}
                                          isClearable={false}
                                        />
                                      ) : (
                                        <div>
                                          Overall Scale:{" "}
                                          {
                                            assesmentScaleConfig[
                                              currentData?.overallScaleId
                                            ]?.scaleName
                                          }
                                        </div>
                                      ))}

                                    {(currentData?.assessmentType ===
                                      "rubricsBasedAssessment" ||
                                      currentData?.assessmentType === "both") &&
                                      (isEditable ? (
                                        <Select
                                          options={assessmentScaleOptions}
                                          value={assessmentScaleOptions?.find(
                                            (opt) =>
                                              opt.value ===
                                              newEditedData?.rubricsScaleId,
                                          )}
                                          onChange={(option) => {
                                            setNewEditedData((prev) => ({
                                              ...prev,
                                              rubricsScaleId: option?.value,
                                            }));
                                          }}
                                          placeholder={"Rubrics scale"}
                                          styles={{
                                            control: { minWidth: "200px" },
                                          }}
                                          isClearable={false}
                                        />
                                      ) : (
                                        <div>
                                          Rubrics Scale:{" "}
                                          {
                                            assesmentScaleConfig[
                                              currentData?.rubricsScaleId
                                            ]?.scaleName
                                          }
                                        </div>
                                      ))}
                                  </div>
                                </td>
                                <td>
                                  {isEditable ? (
                                    <Select
                                      options={assessorOptions}
                                      value={
                                        assessorOptions
                                          ?.flatMap(
                                            (group) => group.options || [],
                                          )
                                          .find(
                                            (option) =>
                                              (option?.subRoleId &&
                                                option.subRoleId ===
                                                  newEditedData?.subRoleId) ||
                                              option?.value ===
                                                newEditedData?.accessor,
                                          ) || null
                                      }
                                      onChange={(option) => {
                                        if (option?.subRoleId) {
                                          setNewEditedData((prev) => ({
                                            ...prev,
                                            accessor: null,
                                            roleCode: option?.roleCode,
                                            subRoleId: option?.subRoleId,
                                            roleId: option?.value,
                                          }));
                                        } else {
                                          setNewEditedData((prev) => ({
                                            ...prev,
                                            accessor: option?.value,
                                            roleCode: null,
                                            subRoleId: null,
                                            roleId: null,
                                          }));
                                        }
                                      }}
                                      placeholder={"Select Assessor"}
                                      styles={{
                                        control: { minWidth: "200px" },
                                      }}
                                      isClearable={false}
                                    />
                                  ) : (
                                    staffRole ||
                                    assessorConfig[currentData?.accessor]
                                      ?.people?.name ||
                                    "N/A"
                                  )}
                                </td>
                                <td>
                                  <div
                                    className="flex"
                                    style={{ flexWrap: "wrap", gap: "8px" }}
                                  >
                                    {(currentData?.assessmentType ===
                                      "rubricsBasedAssessment" ||
                                      currentData?.assessmentType ===
                                        "both") && (
                                      <Button
                                        onClick={() => {
                                          setConfigureRubricsAssessmentModal(
                                            true,
                                          );
                                          setSelectedRow(
                                            currentData?.fId ?? currentData?.id,
                                          );
                                          setAssesmentScaleForRubricsConfig(
                                            assesmentScaleConfig[
                                              currentData?.rubricsScaleId
                                            ],
                                          );
                                        }}
                                        variant="outline"
                                        size="sm"
                                      >
                                        Configure Assessment
                                      </Button>
                                    )}

                                    {(currentData?.assessmentType ===
                                      "overallAssessment" ||
                                      currentData?.assessmentType === "both") &&
                                      renderAssessmentScaleLevel(
                                        currentData?.overallScaleId,
                                      )}
                                  </div>
                                </td>
                                <td>
                                  <div className="action-btns">
                                    {currentData?.id && !isEditable && (
                                      <Button
                                        onClick={() => {
                                          setEditableRowId(currentData?.id);
                                          setNewEditedData(currentData);
                                        }}
                                        size="sm"
                                        variant="outline"
                                      >
                                        Edit
                                      </Button>
                                    )}

                                    {currentData?.id && isEditable && (
                                      <>
                                        <Button
                                          onClick={() => {
                                            setEditableRowId(null);
                                            setNewEditedData({});
                                          }}
                                          size="sm"
                                          variant="outline"
                                        >
                                          Cancel
                                        </Button>

                                        <Button
                                          onClick={() =>
                                            handleSaveEdit(currentData?.id)
                                          }
                                          size="sm"
                                        >
                                          Save
                                        </Button>
                                      </>
                                    )}

                                    {!isEditable && (
                                      <Tooltip
                                        title={
                                          isDisabled
                                            ? "You cannot remove this scale configuration because it is either recorded or finalized in the recordings."
                                            : ""
                                        }
                                      >
                                        <div>
                                          <Button
                                            onClick={() => {
                                              removeDataFromTable(
                                                index,
                                                currentData?.fId ??
                                                  currentData?.id,
                                                currentData?.id,
                                              );
                                            }}
                                            disabled={isDisabled}
                                            size="sm"
                                            variant="danger"
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      </Tooltip>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        );
                      })
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={6}>
                          No Data Found
                        </td>
                      </tr>
                    )}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </DragDropContext>
          </table>
        </div>
      </div>

      <CustomModal
        size="md"
        isOpen={configureRubricsAssessmentModal}
        toggle={() => setConfigureRubricsAssessmentModal(false)}
        modalHeader={"Configure Rubrics Based Assessment"}
        children={
          <ConfigureRubricsBasedAssessment
            assessmentScale={assessmentScaleForRubricsConfig}
            rubricsConfig={rubricsConfig}
            setRubricsConfig={setRubricsConfig}
            CustomOption={CustomOption}
            CustomSingleValue={CustomSingleValue}
            setConfigureRubricsAssessmentModal={
              setConfigureRubricsAssessmentModal
            }
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            setDeletedItem={setDeletedItem}
          />
        }
      />
    </>
  );
};

export default AssessmentConfiguration;
